import React, { useState, useMemo } from 'react';
import CollectionPreferences, {
	CollectionPreferencesProps,
} from '@amzn/awsui-components-console/collection-preferences';
import Header from '@amzn/awsui-components-console/header';
import Pagination from '@amzn/awsui-components-console/pagination';
import Button from '@amzn/awsui-components-console/button';
import ButtonDropdown from '@amzn/awsui-components-console/button-dropdown';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import PropertyFilter, {
	PropertyFilterProps,
} from '@amzn/awsui-components-console/property-filter';
import Table from '@amzn/awsui-components-console/table';
import Modal from '@amzn/awsui-components-console/modal';
import Link from '@amzn/awsui-components-console/link';
import Toggle from '@amzn/awsui-components-console/toggle';
import Container from '@amzn/awsui-components-console/container';
import Box from '@amzn/awsui-components-console/box';
import Flashbar, {
	FlashbarProps,
} from '@amzn/awsui-components-console/flashbar';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { useHistory } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import { getDefaultStripedRows } from 'src/stores/uiPreferencesStore';
import { useFavoritesStore } from 'src/stores/favoriteWorkflowsStore/favoritesStore';
import {
	PropertyFilteri18nStrings,
} from 'src/constants/ComponentConstants';
import {
	FILTERING_PROPERTIES,
	getFilteringOptions,
	filterWorkflows,
} from './ManageWorkflowFilterConfig';
import {
	COLUMN_DEFINITIONS,
	DEFAULT_PREFERENCES,
	COLLECTION_PREFERENCES,
	VIEW_FILTER,
	ViewFilterType,
} from './ManageWorkflowConstants';
import WorkflowDetails from './WorkflowDetailsView';
import { TableWithNoData } from 'src/components/commonComponents/table/tableWithNoData';
import { WorkflowDefinition } from 'src/@types/selfServeTypes/workflowDefinition.interface';
import { useAuthContext } from 'src/providers/AuthContext';
import { useWorkflowData, invalidateWorkflowCache } from '../commonComponents/WorkflowTable/useWorkflowData';
import { invalidatePermissionCache } from '../commonComponents/WorkflowTable/workflowPermissionUtils';
import { useManageWorkflowPermissions } from './useManageWorkflowPermissions';
import { useCopyWorkflowPermissions } from './useCopyWorkflowPermissions';
import { useSelectedWorkflowEditPermission } from './useSelectedWorkflowEditPermission';
import CopyWorkflowModal from '../commonComponents/CopyWorkflowModal/CopyWorkflowModal';
import useFetchOnUserActionUsingSDS from 'src/hooks/useFetchOnUserActionUsingSDS';
import { SNOW_NXT_WORKFLOW_ENDPOINT } from 'src/config';
import { useWorkflowCreatePermission } from 'src/hooks/useWorkflowCreatePermission';

export default function ManageWorkflowData() {
	const [preferences, setPreferences] =
		useState<Partial<CollectionPreferencesProps.Preferences>>(
			() => ({ ...DEFAULT_PREFERENCES, stripedRows: getDefaultStripedRows() })
		);
	const [selectedWorkflows, setSelectedWorkflows] = useState<
		WorkflowDefinition[]
	>([]);
	const [selectedWorkflowName, setSelectedWorkflowName] =
		useState<WorkflowDefinition>();

	const [visible, setVisible] = useState(false);
	const [copyModalVisible, setCopyModalVisible] = useState(false);
	const [viewFilter, setViewFilter] = useState<ViewFilterType>(VIEW_FILTER.ALL);
	const [flashBarItems, setFlashBarItems] = useState<
		FlashbarProps.MessageDefinition[]
	>([]);

	const [propertyFilterQuery, setPropertyFilterQuery] = useState<PropertyFilterProps.Query>({
		tokens: [],
		operation: 'and',
	});
	const authContext = useAuthContext();
	const currentUserName = authContext?.userName;
	const history = useHistory();

	const { onClickCB: copyWorkflowCB, result: copyResult } =
		useFetchOnUserActionUsingSDS<{ workflowId: string }>();

	const {
		isLoading: listWorkflowDefinitionLoading,
		error: listWorkflowDefinitionError,
		allWorkflows,
		refetch: refetchWorkflows,
	} = useWorkflowData();

	const { editableWorkflows, isCheckingPermissions, permissionError } =
		useManageWorkflowPermissions(allWorkflows, viewFilter, currentUserName);

	const { canCopyWorkflow } = useCopyWorkflowPermissions(currentUserName);
	const { hasCreatePermission } = useWorkflowCreatePermission(currentUserName);

	const hasEditPermission = selectedWorkflows.length > 0
		&& !isCheckingPermissions
		&& editableWorkflows.some((w) => w.workflowId === selectedWorkflows[0].workflowId);

	const { favoriteWorkflowIds, toggleFavorite, isFavorite } = useFavoritesStore();

	const listWorkflowData = useMemo(() => {
		if (viewFilter === VIEW_FILTER.USER) {
			return editableWorkflows;
		}

		return allWorkflows;
	}, [allWorkflows, viewFilter, editableWorkflows]);

	const filteringOptions = useMemo(() => getFilteringOptions(listWorkflowData, favoriteWorkflowIds, currentUserName), [listWorkflowData, favoriteWorkflowIds, currentUserName]);

	const propertyFilteredData = useMemo(
		() => filterWorkflows(listWorkflowData, propertyFilterQuery),
		[listWorkflowData, propertyFilterQuery]
	);

	const {
		items,
		filteredItemsCount,
		collectionProps,
		paginationProps,
	} = useCollection(propertyFilteredData, {
		filtering: {
			empty: (
				<TableWithNoData
					message={
						listWorkflowDefinitionError
							? listWorkflowDefinitionError
							: viewFilter === VIEW_FILTER.USER && permissionError
							? permissionError
							: 'No instances to display.'
					}
				/>
			),
			noMatch: <TableWithNoData message={'No results found for your query'} />,
		},
		pagination: { pageSize: preferences.pageSize },
		sorting: {},
	});

	const COLUMN_DEFINITIONS_NAME = {
		id: 'workflowDefinitionName',
		header: 'Workflow Name',
		cell: (item) => (
			<Link
				data-testid={`show_workflow_${item.workflowDefinitionName}`}
				variant="primary"
				onFollow={(e) => {
					e.preventDefault();
					setSelectedWorkflowName(item);
					setVisible(true);
				}}
			>
				{item.workflowDefinitionName}
			</Link>
		),
		minWidth: '180px',
		sortingField: 'workflowDefinitionName',
	};

	const handleCopyWorkflow = async (newWorkflowName: string) => {
		const selectedWorkflow = selectedWorkflows[0];

		if (!selectedWorkflow) return;

		const response = await copyWorkflowCB<{ workflowId: string }>({
			path: `/workflows/${selectedWorkflow.workflowId}/copy`,
			method: 'POST',
			url: SNOW_NXT_WORKFLOW_ENDPOINT,
			body: {
				newWorkflowName,
				createdBy: currentUserName,
				requestId: uuidv4(),
			},
		});

		if (!response.error) {
			const newWorkflowId = response.workflowId;

			invalidateWorkflowCache();
			invalidatePermissionCache();
			setCopyModalVisible(false);

			setFlashBarItems([
				{
					type: 'success',
					content: `Successfully copied workflow to "${newWorkflowName}". Redirecting to edit page...`,
					dismissible: true,
					onDismiss: () => setFlashBarItems([]),
				},
			]);

			history.push(`/createWorkflow?workflowId=${newWorkflowId}`);
		} else {
			let errorMessage = response.error || 'An unknown error occurred';

			if (errorMessage.includes('WorkflowDefinitionName is already in use')) {
				const match = errorMessage.match(
					/\[([^\]]+)\] as WorkflowDefinitionName is already in use/
				);
				const duplicateName = match ? match[1] : newWorkflowName;

				errorMessage = `Workflow name "${duplicateName}" already exists. Please choose a different name.`;
			}

			return errorMessage;
		}
	};

	interface TableHeaderProps {
		selectedWorkflows: WorkflowDefinition[];
		counter: string;
	}

	const TableHeader = ({ selectedWorkflows, counter }: TableHeaderProps) => {
		const selectedWorkflow =
			selectedWorkflows.length > 0 ? selectedWorkflows[0] : null;

		const { canEdit: canCustomizeLayout } = useSelectedWorkflowEditPermission(
			selectedWorkflow?.workflowId,
			currentUserName
		);

		return (
			<Header
				variant="awsui-h1-sticky"
				counter={counter}
				info={
					viewFilter !== VIEW_FILTER.USER ? (
						<Link variant="info" href="#/manageWorkflows/workflowDashboard">
							Dashboard
						</Link>
					) : undefined
				}
				actions={
					<SpaceBetween direction="horizontal" size="s">
						<Button
							variant="icon"
							disabled={!selectedWorkflow}
							iconName={selectedWorkflow && isFavorite(selectedWorkflow.workflowId) ? 'star-filled' : 'star'}
							ariaLabel={selectedWorkflow && isFavorite(selectedWorkflow.workflowId) ? 'Remove from favorites' : 'Add to favorites'}
							data-testid="favorite-toggle-button"
							onClick={() => {
								if (selectedWorkflow) toggleFavorite(selectedWorkflow.workflowId);
							}}
						/>
						<ButtonDropdown
							disabled={!selectedWorkflow}
							items={[
								{
									id: 'copy',
									text: 'Copy to New',
									disabled: !canCopyWorkflow,
									disabledReason: !canCopyWorkflow
										? 'You do not have permission to copy workflows'
										: undefined,
								},
								{
									id: 'manage-task-configuration',
									text: 'Manage Task Configuration',
									disabled: !hasEditPermission,
									disabledReason: !hasEditPermission
										? 'You do not have edit permission for this workflow'
										: undefined,
								},
								{
									id: 'customize-layout',
									text: 'Customize Layout',
									disabled: !canCustomizeLayout,
									disabledReason: !canCustomizeLayout
										? 'You do not have edit permissions for this workflow'
										: undefined,
								},
								{
									id: 'manage-permissions',
									text: 'Manage Permissions',
									href: selectedWorkflow
										? `https://bindles.amazon.com/resource/${selectedWorkflow.bindleId}`
										: '#',
									external: true,
								},
								{
									id: 'simulate',
									text: 'Simulate',
								},
								{
									id: 'compare',
									text: 'Compare with...',
								},
							]}
							onItemClick={({ detail }) => {
								if (detail.id === 'simulate' && selectedWorkflow) {
									history.push(
										`/simulateWorkflow?workflowId=${selectedWorkflow.workflowId}&workflowName=${encodeURIComponent(selectedWorkflow.workflowDefinitionName)}`
									);
								} else if (detail.id === 'compare' && selectedWorkflow) {
									history.push(
										`/compareWorkflows?left=${selectedWorkflow.workflowId}`
									);
								} else if (detail.id === 'copy') {
									setCopyModalVisible(true);
								} else if (detail.id === 'manage-task-configuration' && selectedWorkflow) {
									history.push(`/taskConfiguration?workflowType=${encodeURIComponent(selectedWorkflow.workflowDefinitionName)}`);
								} else if (detail.id === 'customize-layout' && selectedWorkflow) {
									history.push(`/layoutEditor?workflowId=${selectedWorkflow.workflowId}`);
								}
							}}
						>
							Actions
						</ButtonDropdown>
						<Button
							disabled={!selectedWorkflow}
							href={
								selectedWorkflow
									? `#/viewWorkflow?workflowId=${selectedWorkflow.workflowId}`
									: '#'
							}
						>
							View
						</Button>
						<Button
							disabled={!selectedWorkflow || isCheckingPermissions || !hasEditPermission}
							disabledReason={
								!selectedWorkflow
									? 'Select a workflow to edit'
									: isCheckingPermissions
									? 'Checking permissions...'
									: !hasEditPermission
									? 'You do not have edit permission for this workflow'
									: undefined
							}
							href={
								selectedWorkflow && !isCheckingPermissions && hasEditPermission
									? `#/createWorkflow?workflowId=${selectedWorkflow.workflowId}`
									: '#'
							}
						>
							Edit
						</Button>
						<Button
							data-testid="create-workflow-button"
							href={hasCreatePermission ? '#/createWorkflow' : '#'}
							variant="primary"
							disabled={!hasCreatePermission}
							disabledReason={
								!hasCreatePermission
									? 'You do not have permission to create workflows'
									: undefined
							}
						>
							Create Workflow
						</Button>
					</SpaceBetween>
				}
			>
				Manage Workflows{' '}
				{viewFilter === VIEW_FILTER.USER ? '- My Workflows' : '- All Workflows'}
			</Header>
		);
	};

	return (
		<SpaceBetween direction="vertical" size="l">
			<Container>
				<Flashbar items={flashBarItems} />
				<SpaceBetween direction="vertical" size="l">
				{selectedWorkflowName && (
					<Modal
						size="large"
						data-testid="view-workflow-buttons-modal"
						onDismiss={() => setVisible(false)}
						visible={visible}
						header={`${selectedWorkflowName.workflowDefinitionName} Workflow Details`}
						footer={<WorkflowDetails workflowData={selectedWorkflowName} />}
					/>
				)}
				<CopyWorkflowModal
					visible={copyModalVisible}
					onDismiss={() => setCopyModalVisible(false)}
					onConfirm={handleCopyWorkflow}
					isCopying={copyResult.isLoading}
					sourceWorkflowName={
						selectedWorkflows[0]?.workflowDefinitionName || ''
					}
				/>
				<Table
					{...collectionProps}
					resizableColumns
					stickyHeader
					loadingText={
						!listWorkflowDefinitionLoading &&
						viewFilter === VIEW_FILTER.USER &&
						isCheckingPermissions
							? 'Checking permissions...'
							: 'Loading resources'
					}
					loading={
						listWorkflowDefinitionLoading ||
						(viewFilter === VIEW_FILTER.USER && isCheckingPermissions)
					}
					columnDefinitions={[COLUMN_DEFINITIONS_NAME, ...COLUMN_DEFINITIONS]}
					items={items}
					wrapLines
					stripedRows={preferences.stripedRows}
					header={
						<>
							<Box padding={{ top: 'xxs' }}>
								<TableHeader
									selectedWorkflows={selectedWorkflows}
									counter={`(${filteredItemsCount})`}
								/>
							</Box>
							<Box padding={{ vertical: 's' }}>
								<Toggle
									checked={viewFilter === VIEW_FILTER.USER}
									onChange={({ detail }) =>
										setViewFilter(
											detail.checked ? VIEW_FILTER.USER : VIEW_FILTER.ALL
										)
									}
								>
									Show only workflows for which {currentUserName} has edit
									permissions
								</Toggle>
							</Box>
						</>
					}
					columnDisplay={preferences.contentDisplay}
					variant="full-page"
					pagination={
						<SpaceBetween direction="horizontal" size="xs">
							<Button
								variant="icon"
								iconName="refresh"
								ariaLabel="Refresh workflows and permissions"
								data-testid="refresh-workflows-button"
								disabled={listWorkflowDefinitionLoading || isCheckingPermissions}
								onClick={() => {
									invalidateWorkflowCache();
									invalidatePermissionCache();
									refetchWorkflows();
								}}
							/>
							<Pagination {...paginationProps} />
						</SpaceBetween>
					}
					selectionType="single"
					preferences={
						<CollectionPreferences
							{...COLLECTION_PREFERENCES}
							preferences={preferences}
							onConfirm={({ detail }) => setPreferences(detail)}
						/>
					}
					selectedItems={selectedWorkflows}
					onSelectionChange={({ detail }) => {
						setSelectedWorkflows(detail.selectedItems);
					}}
					onRowClick={({ detail }) => {
						setSelectedWorkflows([detail.item]);
					}}
					filter={
						<PropertyFilter
							query={propertyFilterQuery}
							onChange={({ detail }) => {
								const tokensMap = new Map<string, PropertyFilterProps.Token>();
								const freeTextTokens: PropertyFilterProps.Token[] = [];
								(detail.tokens as PropertyFilterProps.Token[]).forEach((token) => {
									if (token.propertyKey) {
										tokensMap.set(token.propertyKey, token);
									} else {
										freeTextTokens.push(token);
									}
								});
								setPropertyFilterQuery({
									...detail,
									tokens: [...freeTextTokens, ...Array.from(tokensMap.values())],
								});
							}}
							filteringProperties={FILTERING_PROPERTIES}
							filteringOptions={filteringOptions}
							hideOperations
							i18nStrings={{
								...PropertyFilteri18nStrings,
								filteringPlaceholder: 'Search workflows or filter by property',
								groupPropertiesText: 'Properties',
								clearFiltersText: 'Clear filters',
							}}
							countText={`${filteredItemsCount} ${filteredItemsCount === 1 ? 'match' : 'matches'}`}
							data-testid="manage-workflow-property-filter"
						/>
					}
				/>
			</SpaceBetween>
			</Container>
		</SpaceBetween>
	);
}
